/**
 * ConventionRedeemPage
 *
 * MAESTRO:CONACCESS P4 — per MDM-CONACCESS-001 Section 6 + 6.5 + 18 P4.
 *
 * Public landing page for the printed QR. URL shape: `/c/:token`.
 * Flow: token captured from URL → user taps Allow Location → browser
 * geolocation requested → POST /api/convention-access/redeem → success
 * banner OR friendly rejection message + retry guidance.
 *
 * iOS Safari requires a user gesture before geolocation; the explicit
 * tap-to-grant button ensures the prompt fires reliably (Section 6.5).
 */

import { useCallback, useMemo, useState } from 'react';
import { redeemQRToken } from '../services/conventionAccessApi';
import { getFingerprintHash } from '../services/fingerprintService';

const STATUS = {
  IDLE: 'idle',
  LOCATING: 'locating',
  REDEEMING: 'redeeming',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

const REJECTION_MESSAGES = {
  geolocation_denied:
    "We need your location to confirm you're at the venue. Maestro convention access is for collectors physically at the show. Tap Allow and try again.",
  out_of_radius:
    "You're outside the venue radius — please move closer to the booth.",
  accuracy_too_low:
    "We couldn't get a precise location. Try moving to a window or stepping outside briefly.",
  expired_token:
    'This QR is no longer valid. Stop by the booth — we issue a fresh code each morning.',
  invalid_signature:
    'This QR could not be verified. Stop by the booth for a fresh code.',
  session_ended:
    'This event has ended — see you next time! Watch the homepage banner for upcoming Maestro shows.',
  qr_token_exhausted:
    'Today\'s redemption ceiling is full. Tell the booth — we\'ll print a fresh code.',
  qr_per_fingerprint_exceeded:
    "You've already redeemed this code today. Stop by the booth if you need another grant.",
  fingerprint_required:
    'Browser fingerprinting failed. Try a regular (non-private) browser window.',
};

function pickRejectionMessage(reason, fallback) {
  return REJECTION_MESSAGES[reason] || fallback || 'Redemption failed — please try again.';
}

function getTokenFromPath(pathname) {
  const match = /^\/c\/([^/?#]+)/.exec(pathname);
  return match ? decodeURIComponent(match[1]) : null;
}

export default function ConventionRedeemPage({ pathname }) {
  const path = pathname || window.location.pathname;
  const token = useMemo(() => getTokenFromPath(path), [path]);

  const [status, setStatus] = useState(token ? STATUS.IDLE : STATUS.REJECTED);
  const [acceptedDetail, setAcceptedDetail] = useState(null);
  const [rejection, setRejection] = useState(
    token
      ? null
      : { reason: 'invalid_signature', message: REJECTION_MESSAGES.invalid_signature },
  );

  const handleRedeem = useCallback(async () => {
    setStatus(STATUS.LOCATING);
    setRejection(null);

    if (!('geolocation' in navigator)) {
      setStatus(STATUS.REJECTED);
      setRejection({
        reason: 'geolocation_denied',
        message: REJECTION_MESSAGES.geolocation_denied,
      });
      return;
    }

    let position;
    try {
      position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 15_000,
        });
      });
    } catch {
      setStatus(STATUS.REJECTED);
      setRejection({
        reason: 'geolocation_denied',
        message: REJECTION_MESSAGES.geolocation_denied,
      });
      return;
    }

    setStatus(STATUS.REDEEMING);
    let fingerprintHash = null;
    try {
      fingerprintHash = await getFingerprintHash();
    } catch {
      /* anonymous redemptions still proceed; backend will reject if needed */
    }

    try {
      const result = await redeemQRToken({
        token,
        userLat: position.coords.latitude,
        userLon: position.coords.longitude,
        accuracyM: position.coords.accuracy ?? 0,
        fingerprintHash,
      });
      setStatus(STATUS.ACCEPTED);
      setAcceptedDetail(result);
    } catch (err) {
      setStatus(STATUS.REJECTED);
      setRejection({
        reason: err?.rejection_reason || 'unknown',
        message: pickRejectionMessage(err?.rejection_reason, err?.message),
      });
    }
  }, [token]);

  return (
    <main
      data-testid="convention-redeem-page"
      className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col items-center justify-center px-4 py-12"
    >
      <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl p-6 sm:p-8 space-y-6">
        <header className="text-center space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Maestro Convention Access
          </h1>
          <p className="text-sm text-zinc-400" aria-live="polite">
            {status === STATUS.IDLE && 'Tap below to confirm you\'re at the booth.'}
            {status === STATUS.LOCATING && 'Capturing your location…'}
            {status === STATUS.REDEEMING && 'Verifying with the booth…'}
            {status === STATUS.ACCEPTED && 'You\'re in!'}
            {status === STATUS.REJECTED && 'We hit a snag.'}
          </p>
        </header>

        {status === STATUS.IDLE && (
          <button
            type="button"
            onClick={handleRedeem}
            data-testid="redeem-allow-location"
            className="w-full bg-orange-500 hover:bg-orange-400 text-zinc-950 font-semibold py-3 rounded-lg transition"
          >
            Allow Location & Redeem
          </button>
        )}

        {(status === STATUS.LOCATING || status === STATUS.REDEEMING) && (
          <div
            data-testid="redeem-spinner"
            role="status"
            aria-live="polite"
            className="flex justify-center items-center py-4"
          >
            <span className="inline-block w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {status === STATUS.ACCEPTED && acceptedDetail && (
          <section
            data-testid="redeem-success"
            className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-4 space-y-2"
          >
            <h2 className="text-emerald-300 font-semibold text-lg">
              Welcome to {acceptedDetail.venue_name}!
            </h2>
            <p className="text-sm text-zinc-300">
              {acceptedDetail.welcome_message ||
                'Your convention burst access is now active for the next 4 hours.'}
            </p>
            <p className="text-xs text-zinc-400">
              Distance to booth: ~{acceptedDetail.haversine_distance_m}m
            </p>
            <a
              href="/grade"
              className="inline-block mt-2 bg-orange-500 hover:bg-orange-400 text-zinc-950 font-semibold px-4 py-2 rounded-lg"
            >
              Start Grading
            </a>
          </section>
        )}

        {status === STATUS.REJECTED && rejection && (
          <section
            data-testid="redeem-rejection"
            role="alert"
            className="bg-red-500/10 border border-red-500/40 rounded-lg p-4 space-y-3"
          >
            <p className="text-red-200 text-sm">{rejection.message}</p>
            {token && (
              <button
                type="button"
                onClick={handleRedeem}
                data-testid="redeem-retry"
                className="bg-zinc-100 hover:bg-white text-zinc-950 font-semibold px-4 py-2 rounded-lg text-sm"
              >
                Try again
              </button>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
